import type { Immutable } from "immer";
import React, { useEffect } from "react";
import { TSIAlignedRichTextBlock } from "tsi-common-react/src/components/TSIAlignedRichTextBlock/index";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { useRovingTabList } from "tsi-common-react/src/hooks/useRovingTabList";
import { useLazyBackground } from "tsi-common-react/src/utils/lazyBackground";

import type { TabFeatureBlock as TabFeatureBlockValue } from "@reactivated";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

/**
 * Faithful port of the legacy server-rendered TP-local
 * ``cms/blocks/tab-feature-block.html`` (#33459 / SSR-V2 Phase 1).
 *
 * Tab switching (roving tabindex, click, keyboard, and URL-hash deep-linking)
 * is owned here via the shared ``useRovingTabList`` hook and component state:
 * the active button carries ``aria-selected="true"`` + ``tabIndex=0`` and its
 * ``[role="tabpanel"]`` drops the ``hidden`` attribute. Each ``panel_id`` must
 * equal the matching button's ``aria-controls``; the element ids
 * (``tab_id`` / ``panel_id``) are precomputed server-side
 * (``tab_feature_block.py``: Django ``slugify`` + 1-based loop counter) so they
 * stay byte-identical to existing anchor links (also the hash deep-link
 * targets). The horizontally-scrolling tablist keeps the active tab in view.
 *
 * Images mirror the legacy ``{% wagtail_image %}`` / ``{% image %}`` renditions:
 *   - section ``data-background-image`` = ``background_image`` ``width-2000``
 *     rendition url (lazy-background; self-managed via ``useLazyBackground``).
 *   - tab thumbnail ``data-background-image`` = ``tab_image.url`` (the ORIGINAL
 *     file url; the legacy template used ``tab_image.file.url``).
 *   - tab-content / feature-list / icon images = eager ``responsive-img``
 *     ``<img>`` with a srcset built from the default specs
 *     (``width-250,width-500,width-1000``), the same renditions the legacy
 *     inclusion tag emitted (GalleryCarouselBlock precedent).
 *
 * The ``tab_content`` StreamBlock dispatches by ``item.type``: ``image_chooser_block``
 * and ``feature_list`` are special-cased (matching the legacy ``{% if %}``);
 * everything else (``aligned_rich_text_block`` via the shared ported component,
 * ``video_block`` EmbedBlock as a bare embed string) falls through the ``else``
 * (legacy ``{% include_block block %}``). Each item is a DIRECT child of the
 * ``<article>`` so the flex split + pill ordering (which target ``> section`` /
 * ``> [class*="image"]``) match the legacy server render.
 */

type Value = TabFeatureBlockValue;
type FeatureTab = NonNullable<Value["tabs"]>[number];
type TabImage = NonNullable<FeatureTab["tab_image"]>;
type TabContentItem = NonNullable<FeatureTab["tab_content"]>[number];
type FeatureListItem = NonNullable<
    Extract<TabContentItem, { type: "feature_list" }>["value"]["feature_list"]
>[number];

// Default ``wagtail_image`` specs the legacy inclusion tag rendered for every
// tab-content image (``width-250,width-500,width-1000``).
const RESPONSIVE_IMAGE_SPECS = [
    "width-250",
    "width-500",
    "width-1000",
] as const;

/**
 * Eager ``responsive-img`` ``<img>`` mirroring ``{% wagtail_image image
 * alt=tab_name class="responsive-img" %}`` under SSR (puppeteer/SSR forces
 * eager): ``src`` = the largest available rendition, ``alt`` = the tab name
 * (the legacy ``alt=tab.tab_name`` arg).
 *
 * No ``srcSet``: under the puppeteer UA the legacy ``wagtail_image`` tag takes
 * the non-lazy branch of ``lazy-wagtail-image.html``, which emits a bare
 * ``<img alt src={largest}>`` with NO ``srcset`` attribute (the ``data-srcset``
 * lives only in the lazy branch). Emitting a ``srcSet`` with ``w`` descriptors
 * and no ``sizes`` makes the browser default ``sizes`` to ``100vw`` and lay the
 * image out at the full container width instead of capping at the rendition's
 * intrinsic ``width-1000`` -- which made the unconstrained ``responsive-img``
 * (``max-width:100%; height:auto``) render TALLER than the legacy server image
 * in the pill theme's full-width image column (Desktop pill regression,
 * #33459). Dropping ``srcSet`` reproduces the legacy single-rendition ``src``
 * sizing exactly and matches the established eager-``<img src=largest>`` port
 * convention.
 */
const ResponsiveImage = (
    props: Immutable<{ image: TabImage; alt: string }>,
): React.JSX.Element => {
    const image = props.image;
    const rends = RESPONSIVE_IMAGE_SPECS.map((spec) => image.renditions[spec])
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        .filter((rend): rend is NonNullable<typeof rend> => Boolean(rend));
    const largest = rends.length ? rends[rends.length - 1] : null;
    return (
        <img
            alt={props.alt}
            className="responsive-img"
            src={largest?.url ?? image.url}
            loading="lazy"
            decoding="async"
        />
    );
};

/**
 * Inline ``svg-django/circle-blue-question-mark.svg`` sprite (byte-for-byte the
 * tooltip icon the legacy ``{% include 'cms/components/svg.html' %}`` rendered).
 * The legacy ``description=str_tempur_pedic_home`` context var was unbound -> the
 * screenreader span was never emitted, and the sprite received
 * ``class="tab-feature-block__list-item__tooltip__icon"`` from the include
 * context. ``title`` / ``aria-hidden`` were likewise unbound.
 */
const TooltipIcon = (): React.JSX.Element => (
    <svg
        className="tab-feature-block__list-item__tooltip__icon"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="10"
        viewBox="0 0 10 10"
    >
        <g data-name="Group 14013" transform="translate(-981 -282)">
            <circle
                cx="5"
                cy="5"
                r="5"
                fill="#326295"
                data-name="Ellipse 2"
                transform="translate(981 282)"
            />
            <text
                fill="#fff"
                data-name="?"
                fontFamily="inherit"
                fontSize="8"
                fontWeight="700"
                transform="translate(984 290)"
            >
                <tspan x="0" y="0">
                    ?
                </tspan>
            </text>
        </g>
    </svg>
);

const FeatureListItemElem = (
    props: Immutable<{ feature: FeatureListItem; altName: string }>,
): React.JSX.Element => {
    const feature = props.feature;
    return (
        <li className="tab-feature-block__list-item">
            <figure className="tab-feature-block__list-item__icon">
                {feature.icon ? (
                    <ResponsiveImage image={feature.icon} alt={props.altName} />
                ) : null}
            </figure>
            <div className="tab-feature-block__list-item__text">
                {feature.text}
            </div>
            {/* Legacy gate: ``{% if feature.tooltip != '' %}``. */}
            {feature.tooltip ? (
                <div className="tab-feature-block__list-item__tooltip">
                    <TooltipIcon />
                    <div className="tab-feature-block__list-item__tooltip__text">
                        {feature.tooltip}
                    </div>
                </div>
            ) : null}
        </li>
    );
};

const TabContentItemElem = (
    props: Immutable<{ item: TabContentItem; altName: string }>,
): React.JSX.Element => {
    const item = props.item;
    if (item.type === "image_chooser_block") {
        return (
            <div className="tab-feature-block__image">
                {item.value ? (
                    <ResponsiveImage image={item.value} alt={props.altName} />
                ) : null}
            </div>
        );
    }
    if (item.type === "feature_list") {
        const fv = item.value;
        const features = fv.feature_list ?? [];
        return (
            <div className="tab-feature-block__list-container">
                <figure className="tab-feature-block__list-image">
                    {fv.main_image ? (
                        <ResponsiveImage
                            image={fv.main_image}
                            alt={props.altName}
                        />
                    ) : null}
                </figure>
                <ul className="tab-feature-block__list-items">
                    {features.map((feature, i) => (
                        <FeatureListItemElem
                            key={i}
                            feature={feature}
                            altName={props.altName}
                        />
                    ))}
                </ul>
            </div>
        );
    }
    if (item.type === "aligned_rich_text_block") {
        // Legacy ``else`` branch: ``{% include_block block %}`` -> the shared,
        // already-ported AlignedRichTextBlock (emits a bare ``<section>``, the
        // direct article child the flex/pill SCSS targets via ``> section``).
        return <TSIAlignedRichTextBlock value={item.value} />;
    }
    // video_block (EmbedBlock): the legacy ``else`` branch rendered the embed
    // HTML string (which may carry the Wistia async <script>) bare via
    // ``{% include_block block %}``; inject it directly (MediaBlock precedent).
    return <div dangerouslySetInnerHTML={{ __html: item.value ?? "" }} />;
};

/**
 * The circular tab thumbnail: a per-tab lazy background so each button's image
 * owns its own ``useLazyBackground`` IntersectionObserver (eager under
 * puppeteer/SSR). Extracted to a child so every mounted mask gets one ref/hook.
 */
const TabCircleMask = (
    props: Immutable<{ imageUrl: string | undefined }>,
): React.JSX.Element => {
    const ref = React.useRef<HTMLDivElement | null>(null);
    useLazyBackground(ref);
    return (
        <div
            ref={ref}
            className="tab-feature-block__circle-mask lazy-background"
            data-background-image={props.imageUrl}
        />
    );
};

const TabPanel = (
    props: Immutable<{ tab: FeatureTab; active: boolean }>,
): React.JSX.Element => {
    const tab = props.tab;
    const items = tab.tab_content ?? [];
    const altName = tab.tab_name ?? "";
    return (
        <article
            id={tab.panel_id}
            role="tabpanel"
            tabIndex={0}
            aria-labelledby={tab.tab_id}
            hidden={!props.active}
            className={tab.image_location ? `align-${tab.image_location}` : ""}
        >
            {items.map((item, i) => (
                <TabContentItemElem key={i} item={item} altName={altName} />
            ))}
        </article>
    );
};

export const TabFeatureBlock = (
    props: Immutable<{ value: TabFeatureBlockValue }>,
): React.JSX.Element => {
    const v = props.value;
    const tabs = v.tabs ?? [];
    const textStyle = v.text_style ?? "";
    const bgImage = v.background_image;
    const bgImageUrl = bgImage
        ? (bgImage.renditions["width-2000"]?.url ?? bgImage.url)
        : "";
    // Defer the section's CSS background image until it scrolls into view (eager
    // under puppeteer/SSR) via the shared per-element hook.
    const sectionRef = React.useRef<HTMLElement | null>(null);
    useLazyBackground(sectionRef);
    const { activeIndex, setActiveIndex, getTabProps, tabListRef } =
        useRovingTabList({
            count: tabs.length,
            scrollActiveIntoView: true,
        });

    // Deep-link support: on mount, open the tab named by the URL hash (matched
    // against either the tab button or the panel id). Post-hydration only, so
    // the SSR render stays on tab 0 with no hydration mismatch.
    useEffect(() => {
        const hash = getWindow()?.location.hash;
        if (!hash) {
            return;
        }
        const id = hash.slice(1);
        const idx = tabs.findIndex(
            (tab) => tab.tab_id === id || tab.panel_id === id,
        );
        if (idx >= 0) {
            setActiveIndex(idx);
        }
        // Mount-only: the URL hash is read once, as the legacy on-load bind did.
    }, []);
    return (
        <section
            ref={sectionRef}
            className={concatClassNames([
                "tab-feature-block",
                v.theme?.theme ?? "",
                textStyle,
                // Legacy: ``{% if value.background_style.style %}...{% else
                // %}background--default{% endif %}``.
                v.background_style?.style || "background--default",
                getGlobalSpacingClasses(v.spacing),
                "lazy-background",
            ])}
            data-background-image={bgImageUrl}
        >
            <div className="l-capped-width u-flex-container">
                <header
                    dangerouslySetInnerHTML={{
                        __html: v.header_content ?? "",
                    }}
                />
                <div
                    ref={tabListRef}
                    className="tab-feature-block__tablist"
                    role="tablist"
                    aria-label="Tab Feature Block - Tab List"
                >
                    {tabs.map((tab, i) => (
                        <div key={i}>
                            <button
                                className={
                                    tab.tab_line_break
                                        ? "tab-feature-block--break-spaces"
                                        : undefined
                                }
                                id={tab.tab_id}
                                type="button"
                                role="tab"
                                aria-controls={tab.panel_id}
                                {...getTabProps(i)}
                            >
                                <TabCircleMask imageUrl={tab.tab_image?.url} />
                                <span className={textStyle}>
                                    {tab.tab_name}
                                </span>
                            </button>
                        </div>
                    ))}
                </div>
                <div className="tab-feature-block__content">
                    {tabs.map((tab, i) => (
                        <TabPanel
                            key={i}
                            tab={tab}
                            active={i === activeIndex}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};
